import { useState } from "react";
import "./OperatorLogin.css";
import PasswordInput from "./PasswordInput";

const Icon = ({ d, size = 16, strokeWidth = 1.75, className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    email: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'm22 6-10 7L2 6'],
    lock: ['rect width="18" height="11" x="3" y="11" rx="2" ry="2"', 'path d="M7 11V7a5 5 0 0 1 10 0v4"'],
};

export default function OperatorLogin({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch("/api/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password, portalType: 'operator' }),
            });

            const data = await res.json();

            if (res.ok) {
                if (data.user.userType !== 'operator') {
                    setError("Not authorized. This portal is for Operators only.");
                    setIsLoading(false);
                    return;
                }
                localStorage.setItem("ops-user", JSON.stringify(data.user));
                onLogin(data.user);
            } else {
                setError(data.message || "Invalid credentials. Please try again.");
            }
        } catch (err) {
            setError("Connection failed. Check your server.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-side-decoration operator-theme">
                <div className="decor-content">
                    <h2>Welcome to the Operations Hub</h2>
                    <p>Manage your entire fleet, assignments, vendors, and drivers — all from one powerful control centre.</p>
                </div>
                <div className="animated-blobs">
                    <div className="blob blob-1"></div>
                    <div className="blob blob-2"></div>
                    <div className="blob blob-3"></div>
                </div>
            </div>

            <div className="login-main">
                <div className="login-box">
                    <div className="login-box-header">
                        <h1>Welcome Back</h1>
                        <p>Sign in to your Operator Control Panel</p>
                    </div>

                    <form className="login-form-premium" onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message-shake">
                                <Icon d={['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z']} size={18} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="input-field">
                            <label>Email Address</label>
                            <div className="input-inner">
                                <Icon d={icons.email} size={18} className="input-icon-svg" />
                                <input
                                    type="email"
                                    placeholder="operator@yourdomain.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-field">
                            <div className="label-row">
                                <label>Password</label>
                            </div>
                            <div className="input-inner">
                                <Icon d={['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z']} size={18} className="input-icon-svg" />
                                <PasswordInput
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <button type="submit" className="submit-btn" disabled={isLoading}>
                            {isLoading ? (
                                <span className="loader-text">
                                    <span className="dot-flashing"></span>
                                    Authenticating...
                                </span>
                            ) : (
                                <>
                                    <span>Access Control Panel</span>
                                    <Icon d={['M5 12h14', 'm12 5 7 7-7 7']} size={18} strokeWidth={2} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="login-box-footer">
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <a href="#superadmin-login" className="forgot-link" style={{ color: '#00B2A9', fontWeight: 600 }}>→ Sign in as Super Admin</a>
                            <a href="#client-login" className="forgot-link" style={{ color: '#8b5cf6', fontWeight: 600 }}>→ Sign in as Client</a>
                            <a href="#vendor-login" className="forgot-link" style={{ color: '#10b981', fontWeight: 600 }}>→ Sign in as Vendor Partner</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
